import Channel from '@@types/Channel';
import OrgChartRequest from '@@types/OrgChartRequest';

import { GNB_TABS } from '~/constants';
import { membersStore } from '~/stores/MembersStore';
import { orgsStore } from '~/stores/OrgsStore';
import { api } from '~/stores/providers/Api2';
import { rename, runFutureLastly } from '~/utils/fnUtil/fnUtil';
import { runFutureValue } from '~/utils/monadUtil';
import { calcLastPage } from '~/utils/pageUtil';
import { safeParseJsonText } from '~/utils/textUtil/textUtil';
import Future, { encaseP } from 'fluture';
import { assoc, chain, curry, find, map, pipe, pluck, prop, propEq, tap } from 'ramda';
import { Maybe } from 'ramda-fantasy';

import Actions from '../Actions';

const ORG_MEMBER_PAGE_SIZE = 30;

class OrgsActions {
  constructor(private actions: Actions) {}

  private fetchMembers = (params: { orgId: string; size: number; page?: number }) => {
    return pipe<any, any, any>(
      encaseP(api.member.fetchOrganizationMembers),
      map(rename({ contents: 'members' }))
    )(params);
  };

  private setOrgInfoFromResponse = (orgId, { totalCount, references: { organizationMap = [] } }) => {
    pipe(
      find(propEq('id', orgId)),
      assoc('totalCount', totalCount),
      assoc('page', 0),
      assoc('members', []),
      assoc('lastPage', calcLastPage(totalCount, ORG_MEMBER_PAGE_SIZE)),
      orgsStore.setOrg
    )(organizationMap);
  };

  private setOrgMembersIdFromResponse = (orgId, { members }) => {
    membersStore.mergeMembers(members);
    orgsStore.concatMembers(orgId, pluck('id')(members));
  };

  private handleFetchedInitialMembers = curry((orgId, res) => {
    this.setOrgInfoFromResponse(orgId, res);
    this.setOrgMembersIdFromResponse(orgId, res);
  });

  private handleFetchedMoreMembers = curry((orgId, page, res) => {
    this.setOrgMembersIdFromResponse(orgId, res);
    orgsStore.setPage(orgId, page);
  });

  fetchInitialMembers = (orgId: string) => {
    orgsStore.setIsLoading(orgId, true);
    pipe(
      this.fetchMembers,
      runFutureLastly(Future.of(false).map(orgsStore.setIsLoading(orgId))),
      runFutureValue(this.handleFetchedInitialMembers(orgId))
    )({ orgId, page: 0, size: ORG_MEMBER_PAGE_SIZE });
  };

  fetchMoreMembers = (orgId: string, page: number) => {
    if (orgsStore.isLoading[orgId]) {
      return;
    }
    orgsStore.setIsLoading(orgId, page);
    return pipe<any, any, any, any>(
      this.fetchMembers,
      runFutureLastly(Future.of(false).map(orgsStore.setIsLoading(orgId))),
      map(this.handleFetchedMoreMembers(orgId, page))
    )({ orgId, page, size: ORG_MEMBER_PAGE_SIZE });
  };

  private getOrCreateChannelByMembersId = (membersId: string[]) => {
    if (membersId.length === 1) {
      return this.actions.channelActions.getOrCreateByOpponentId(membersId[0]);
    } else {
      return this.actions.channelActions.getOrCreateGroup(membersId);
    }
  };

  handleOrgChartRequest = (data: OrgChartRequest) => {
    this.getOrCreateChannelByMembersId(data.content).then(
      pipe(
        tap((channel: Channel) => this.actions.channelActions.selectChannel(channel.channelId)),
        tap(this.selectRecentTab)
      ),
      () => {}
    );
  };

  private selectRecentTab = () => {
    this.actions.gnbActions.setGnbTab(GNB_TABS.RECENT);
  };

  private isCreateChannelMessage = (params) => {
    const { source, action } = params;
    return source === 'orgChart' && action === 'openChat' ? Maybe.Just(params) : Maybe.Nothing();
  };

  handleOrgChartFromMessage = (message) => {
    const { source } = message;
    pipe<any, any, any, any, any, any>(
      prop('data'),
      safeParseJsonText,
      chain(this.isCreateChannelMessage),
      map(assoc('orgChartWindow', source)),
      map(this.handleOrgChartRequest)
    )(message);
  };
}

export default OrgsActions;
